/**
 * Projects API Client
 * Handles all communication with the project-service backend
 */

// Types imported from shared-types
export interface Repository {
  url: string;
  branch: string;
  worktreePath?: string;
}

export interface ProjectPorts {
  api: number;
  frontend?: number;
  postgres: number;
  redis: number;
}

export type ProjectType = 'service' | 'app' | 'library';
export type ProjectStatus = 'setup' | 'active' | 'paused' | 'archived';
export type PaneType = 'development' | 'gtm' | 'success';

export interface ProjectBoard {
  id: string;
  projectId: string;
  worktreeId: string;
  boardId: string;
  isPrimary: boolean;
  createdAt: string;
}

export interface ProjectWorktree {
  id: string;
  projectId: string;
  worktreeId: string;
  isPrimary: boolean;
  createdAt: string;
}

export interface Project {
  id: string;
  workspaceId: string;
  name: string;
  slug: string;
  description?: string;
  issuePrefix?: string;
  panes: PaneType[];
  type: ProjectType;
  repository: Repository;
  ports: ProjectPorts;
  status: ProjectStatus;
  boards?: ProjectBoard[];
  worktrees?: ProjectWorktree[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectRequest {
  workspaceId: string;
  name: string;
  slug: string;
  description?: string;
  type: ProjectType;
  panes?: PaneType[];
  issuePrefix?: string;
  repository: {
    url: string;
    branch: string;
  };
}

export interface LinkBoardRequest {
  worktreeId: string;
  boardId: string;
  isPrimary?: boolean;
}

export interface UpdateProjectRequest {
  name?: string;
  type?: ProjectType;
  status?: ProjectStatus;
  panes?: PaneType[];
  issuePrefix?: string;
  repository?: { url: string; branch: string };
}

export interface LinkWorktreeRequest {
  worktreeId: string;
  isPrimary?: boolean;
}

export interface UpdateWorktreeLinkRequest {
  isPrimary?: boolean;
}

export interface ProjectStatusResponse {
  status: ProjectStatus;
  ports: ProjectPorts;
  health: 'healthy' | 'unhealthy' | 'unknown';
}

// API base URL - relative path works with vite proxy (dev) and cloudflare tunnel (prod)
const API_BASE = '/api/projects';

class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // Include cookies for auth
  });

  if (!response.ok) {
    const error = await response.text();
    throw new ApiError(response.status, error || response.statusText);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Projects API
export const projectsApi = {
  /**
   * List all projects for a workspace
   */
  list: async (workspaceId: string): Promise<Project[]> => {
    return request<Project[]>(`/workspace/${workspaceId}`);
  },

  /**
   * List all projects across all workspaces
   */
  listAll: async (): Promise<Project[]> => {
    const res = await request<{ projects: Project[] }>('/');
    return res.projects || [];
  },

  /**
   * List projects filtered by pane
   */
  listByPane: async (pane: PaneType, workspaceId: string): Promise<Project[]> => {
    return request<Project[]>(`/workspace/${workspaceId}?pane=${pane}`);
  },

  /**
   * Get a single project by ID
   */
  get: (id: string): Promise<Project> => {
    return request<Project>(`/${id}`);
  },

  /**
   * Create a new project
   */
  create: (data: CreateProjectRequest): Promise<Project> => {
    return request<Project>('', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update an existing project
   */
  update: (id: string, data: UpdateProjectRequest): Promise<Project> => {
    return request<Project>(`/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a project
   */
  delete: (id: string): Promise<void> => {
    return request<void>(`/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get project status and health
   */
  getStatus: (id: string): Promise<ProjectStatusResponse> => {
    return request<ProjectStatusResponse>(`/${id}/status`);
  },

  /**
   * Setup project (allocate ports, create worktree)
   */
  setup: (id: string): Promise<Project> => {
    return request<Project>(`/${id}/setup`, {
      method: 'POST',
    });
  },

  /**
   * Start project services
   */
  start: (id: string): Promise<{ success: boolean; message: string }> => {
    return request<{ success: boolean; message: string }>(`/${id}/start`, {
      method: 'POST',
    });
  },

  /**
   * Stop project services
   */
  stop: (id: string): Promise<{ success: boolean; message: string }> => {
    return request<{ success: boolean; message: string }>(`/${id}/stop`, {
      method: 'POST',
    });
  },

  /**
   * Link a board to a project
   */
  linkBoard: (id: string, data: LinkBoardRequest): Promise<ProjectBoard> => {
    return request<ProjectBoard>(`/${id}/boards`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Unlink a board from a project
   */
  unlinkBoard: (id: string, boardLinkId: string): Promise<void> => {
    return request<void>(`/${id}/boards/${boardLinkId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get all boards linked to a project
   */
  getBoards: (id: string): Promise<ProjectBoard[]> => {
    return request<ProjectBoard[]>(`/${id}/boards`);
  },

  // ============================================
  // Project-Worktree Linking
  // ============================================

  /**
   * Get all worktrees linked to a project
   */
  getWorktrees: async (id: string): Promise<ProjectWorktree[]> => {
    const res = await request<{ worktrees: ProjectWorktree[] }>(`/${id}/worktrees`);
    return res.worktrees || [];
  },

  /**
   * Link a worktree to a project
   */
  linkWorktree: (id: string, data: LinkWorktreeRequest): Promise<ProjectWorktree> => {
    return request<ProjectWorktree>(`/${id}/worktrees`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Unlink a worktree from a project
   */
  unlinkWorktree: (id: string, linkId: string): Promise<void> => {
    return request<void>(`/${id}/worktrees/${linkId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Set a worktree as primary for a project
   */
  setPrimaryWorktree: (id: string, linkId: string): Promise<ProjectWorktree> => {
    return request<ProjectWorktree>(`/${id}/worktrees/${linkId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isPrimary: true }),
    });
  },

  /**
   * Update a worktree link
   */
  updateWorktreeLink: (id: string, linkId: string, data: UpdateWorktreeLinkRequest): Promise<ProjectWorktree> => {
    return request<ProjectWorktree>(`/${id}/worktrees/${linkId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

export { ApiError };
